import { useCallback } from 'react';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';
import OrderPriceButton from '#/components/UnifiedRfqs/drfq/OrderPriceButton';
import { QtyButton } from '#/components/rfq-common/ActionButtons';
import { BaseRfqAction as RfqAction } from '#/components/UnifiedRfqs/rfqs/action/BaseRfqAction';
import { useUMPlatformCreationDisabled } from '@paradigm/features/src/platform-state/contexts/providers';
import { useTrackAction } from '@paradigm/features/src/analytics';
import type { RfqOrder } from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';

interface RfqOrdersProps {
  readonly onPriceClick?: () => void;
  readonly bid?: RfqOrder;
  readonly ask?: RfqOrder;
  readonly offsetAsk?: string | null;
  readonly offsetBid?: string | null;
  readonly mark: string | null;
  readonly markPriceIv?: string | null;
  readonly isTopQuote?: boolean;
  readonly isBetterThanScreenBid?: boolean;
  readonly isBetterThanScreenAsk?: boolean;
  readonly isMarketRow?: boolean;
}

export default function RfqOrders({
  bid,
  ask,
  onPriceClick,
  offsetAsk,
  offsetBid,
  mark,
  isTopQuote = false,
  isBetterThanScreenAsk,
  isBetterThanScreenBid,
  markPriceIv,
  isMarketRow = false,
}: RfqOrdersProps) {
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const rfq = useDrfqRfq();
  const productCode = rfq.product_codes[0];
  const isDated = isDatedRfqLegs(rfq.legs, productCode);
  const bidQty = bid?.quantity ?? null;
  const askQty = ask?.quantity ?? null;
  const track = useTrackAction();

  const handlePriceClick = useCallback(() => {
    onPriceClick?.();
    if (isMarketRow) {
      track('view quote builder', { auctionType: 'RFQ' });
    }
  }, [isMarketRow, onPriceClick, track]);

  return (
    <RfqAction
      css="grid-area: quotes;"
      bidQty={
        <QtyButton
          quantity={bidQty}
          productCode={productCode}
          isNormalized
          isDated={isDated}
          disabled={isPlatformCreationDisabled}
          hidePlaceholder={!isTopQuote}
        />
      }
      markIv={isTopQuote ? markPriceIv : null}
      mark={mark}
      offsetAsk={offsetAsk}
      offsetBid={offsetBid}
      bidFlashKey={isTopQuote && bid?.isNew === true ? bid.id : undefined}
      askFlashKey={isTopQuote && ask?.isNew === true ? ask.id : undefined}
      bidPrice={
        <OrderPriceButton
          disabled={isPlatformCreationDisabled}
          onClick={handlePriceClick}
          order={bid}
          side="BUY"
          mark={mark}
          hidePlaceholder={!isTopQuote}
        />
      }
      askQty={
        <QtyButton
          disabled={isPlatformCreationDisabled}
          quantity={askQty}
          productCode={productCode}
          isNormalized
          isDated={isDated}
          hidePlaceholder={!isTopQuote}
        />
      }
      askPrice={
        <OrderPriceButton
          disabled={isPlatformCreationDisabled}
          onClick={handlePriceClick}
          order={ask}
          side="SELL"
          mark={mark}
          hidePlaceholder={!isTopQuote}
        />
      }
      isBetterThanScreenBestAsk={isBetterThanScreenAsk}
      isBetterThanScreenBestBid={isBetterThanScreenBid}
    />
  );
}
